// ABOUTME: One realtime session: a centrifuge-js client on the user's channel that buffers the
// ABOUTME: publications, reports the subscription state and asks for a reload when needed.
import type { Options, Subscription, TransportEndpoint } from "centrifuge"
import { Centrifuge, UnauthorizedError } from "centrifuge"
import { createLogger } from "@/model/logger"
import { createEventBuffer } from "./eventBuffer"
import type { RealtimeEvent } from "./events"
import { parseRealtimeEvent } from "./events"
import { jwtSubject } from "./token"

const logger = createLogger("Realtime")

/**
 * A recovery failure hits every client of a node at once (a Redis restart, a
 * long break of the whole office); spreading the reload over two
 * seconds keeps the wide requests from landing as one salvo.
 */
export const RELOAD_JITTER_MS = 2000

/** Two reloads closer than this are one: a reconnect storm must not multiply the wide requests. */
export const RELOAD_MIN_INTERVAL_MS = 30_000

/**
 * First retry after the server ended the connection for good (code 4500,
 * an impersonation revoked). The athlete's own tabs come back with a fresh
 * token; a tab the server keeps ending waits twice as long each time.
 */
export const TERMINAL_RECONNECT_MS = 1000
const TERMINAL_RECONNECT_MAX_MS = 60_000

export interface RealtimeSessionOptions {
  /** Base of the realtime service under the app origin, e.g. `https://app.trainingendurance.com/realtime`. */
  endpoint: string
  /** The signed-in user; the token's subject must be this user, or the session stops. */
  userId: string
  /** Throws UnauthorizedError when the session is gone — the client then stays disconnected. */
  getToken: () => Promise<string>
  /** One buffer flush: every parsed publication of the window. */
  onEvents: (events: RealtimeEvent[]) => void
  /** The channel could not replay what was missed; everything it feeds has to be re-read. */
  onReload: () => void
  /** True while the user's channel is subscribed — the only state in which events arrive. */
  onConnectionChange: (connected: boolean) => void
  /**
   * A session that replaces an earlier one of the same page re-reads
   * everything on its first subscribe: the events of the gap between the two
   * are lost. The page's first session does not — it has just loaded its data.
   */
  reloadOnFirstSubscribe?: boolean
  /** centrifuge options an environment injects: the websocket constructor, reconnect delays. */
  clientOptions?: Partial<Pick<Options, "websocket" | "minReconnectDelay" | "maxReconnectDelay" | "timeout">>
}

export interface RealtimeSession {
  stop: () => void
}

/** Websocket first, HTTP streaming as the fallback — both under the same path prefix. */
/** A relative base (an env without an app URL) resolves against the page origin. */
const serviceBase = (endpoint: string): string =>
  new URL(endpoint, window.location.origin).toString().replace(/\/$/, "")

export function transportEndpoints(endpoint: string): TransportEndpoint[] {
  const base = serviceBase(endpoint)
  return [
    { transport: "websocket", endpoint: `${base.replace(/^http/, "ws")}/connection/websocket` },
    { transport: "http_stream", endpoint: `${base}/connection/http_stream` },
  ]
}

/**
 * Where the http-stream fallback posts the commands it cannot send on its own
 * one-way stream — subscribe above all. Centrifuge defaults it to "/emulation"
 * at the page origin, which no proxy or service prefix covers: the stream
 * would connect and the subscription would never complete.
 */
export function emulationEndpoint(endpoint: string): string {
  return `${serviceBase(endpoint)}/emulation`
}

/**
 * The codes centrifuge itself stops reconnecting on — both bands, exactly as
 * its own check reads them (`(code >= 3500 && code < 4000) || (code >= 4500 &&
 * code < 5000)`). 35xx is the one that happens in practice: an expired or
 * invalid token while the tab was backgrounded. Missing that band meant the
 * client went quiet with nothing to re-arm it, and every poller stayed on the
 * connected cadence behind a dead channel until the page reloaded.
 */
const isTerminalDisconnect = (code: number): boolean => (code >= 3500 && code < 4000) || (code >= 4500 && code < 5000)

export function startRealtimeSession(options: RealtimeSessionOptions): RealtimeSession {
  const { userId, onEvents, onReload, onConnectionChange } = options
  const buffer = createEventBuffer<RealtimeEvent>(onEvents)
  let subscription: Subscription | null = null
  let reloadTimer: ReturnType<typeof setTimeout> | null = null
  let reconnectTimer: ReturnType<typeof setTimeout> | null = null
  let lastReloadAt = Number.NEGATIVE_INFINITY
  let terminalReconnectDelay = TERMINAL_RECONNECT_MS
  let firstSubscribe = true
  let warnedNotRecoverable = false
  let stopped = false

  const scheduleReload = (): void => {
    // One pending reload at a time: a reconnect storm asks many
    // times and gets one answer. It is never dropped, only deferred — there
    // may be no later `subscribed` to ask again, and the changes missed
    // meanwhile would then wait for the poll.
    if (reloadTimer !== null) return
    const sinceLast = Date.now() - lastReloadAt
    const wait =
      sinceLast < RELOAD_MIN_INTERVAL_MS ? RELOAD_MIN_INTERVAL_MS - sinceLast : Math.random() * RELOAD_JITTER_MS
    reloadTimer = setTimeout(() => {
      reloadTimer = null
      lastReloadAt = Date.now()
      onReload()
    }, wait)
  }

  const subscribe = (subject: string): void => {
    // The server checks the token's subject against the id in the channel, so
    // this is the only channel this connection can ever hold.
    const channel = client.newSubscription(`user:#${subject}`)
    subscription = channel
    channel.on("publication", (ctx) => {
      const event = parseRealtimeEvent(ctx.data)
      if (event === null) return
      const at =
        typeof (ctx.data as { at?: unknown }).at === "string" ? Date.parse((ctx.data as { at: string }).at) : Number.NaN
      logger.debug("Event", { type: event.type, deliveryMs: Number.isNaN(at) ? undefined : Date.now() - at })
      buffer.push(event)
    })
    channel.on("subscribed", (ctx) => {
      terminalReconnectDelay = TERMINAL_RECONNECT_MS
      onConnectionChange(true)
      const first = firstSubscribe
      firstSubscribe = false
      if (!ctx.recoverable && !warnedNotRecoverable) {
        warnedNotRecoverable = true
        logger.warn("The channel keeps no history on the server: every subscribe re-reads everything")
      }
      // Recovered: the missed publications are replayed right after this event
      // and go through the buffer like live ones. The page's first subscribe is
      // not a loss either — the page has just loaded its data, and the poll
      // covers the moment between that load and this subscribe; a re-read here
      // would double the heaviest screens on every open. What needs the full
      // re-read is a recovery that failed (a break longer than the channel
      // history, a WebView back from the background, a broker restart), a
      // channel the server cannot recover at all, or a session that replaced
      // an earlier one of this page.
      const lost =
        !ctx.recovered && (ctx.wasRecovering || !ctx.recoverable || (first && options.reloadOnFirstSubscribe === true))
      if (lost) scheduleReload()
    })
    // Subscribing again (the connection dropped) and unsubscribed (the server
    // refused or ended the channel) both mean no events arrive: the pollers
    // must not trust a client that is connected while its channel is dead.
    channel.on("subscribing", () => onConnectionChange(false))
    channel.on("unsubscribed", (ctx) => {
      logger.warn("Unsubscribed from the channel", { code: ctx.code, reason: ctx.reason })
      onConnectionChange(false)
    })
    channel.on("error", (ctx) => logger.warn("Subscription error", { type: ctx.type, error: ctx.error }))
    channel.subscribe()
  }

  const stop = (): void => {
    if (stopped) return
    stopped = true
    // Before the listeners go: `disconnected` is what tells the app to fall
    // back to its slower polling, and this path (an identity mismatch) removes
    // the listener that would otherwise report it — leaving every poller on
    // the connected cadence with no channel behind it.
    onConnectionChange(false)
    if (reloadTimer !== null) clearTimeout(reloadTimer)
    if (reconnectTimer !== null) clearTimeout(reconnectTimer)
    reloadTimer = null
    reconnectTimer = null
    buffer.dispose()
    subscription?.removeAllListeners()
    subscription?.unsubscribe()
    client.removeAllListeners()
    client.disconnect()
  }

  // The channel is the token's subject, not the page's idea of who is signed
  // in: another tab may have switched identity while this page still holds
  // the old `me`. A token for somebody else stops the session for good — this
  // one cannot re-arm itself, so the app runs on polling until the provider
  // notices the new identity and starts a session for it.
  const getToken = async (): Promise<string> => {
    const token = await options.getToken()
    const subject = jwtSubject(token)
    if (subject !== userId) {
      logger.warn(
        "The realtime token belongs to another identity; the session stops until the signed-in user matches",
        { subject, userId },
      )
      stop()
      throw new UnauthorizedError("realtime token subject does not match the signed-in user")
    }
    if (subscription === null && !stopped) subscribe(subject)
    return token
  }

  // centrifuge registers window online/offline listeners per client and never
  // removes them; a stopped client keeps a pair behind. Harmless — its handler
  // only acts while connecting — and identities change a few times per page at most.
  const client = new Centrifuge(transportEndpoints(options.endpoint), {
    getToken,
    emulationEndpoint: emulationEndpoint(options.endpoint),
    ...options.clientOptions,
  })
  client.on("disconnected", (ctx) => {
    logger.debug("Disconnected", { code: ctx.code, reason: ctx.reason })
    onConnectionChange(false)
    // 4500–4999: the server ended the connection for good (an impersonation
    // revoked) and centrifuge does not reconnect on its own. The cached token
    // is dropped so the next attempt asks for a fresh one: the athlete's own
    // tabs come back within a second, the admin's tab is refused and stays down.
    if (!isTerminalDisconnect(ctx.code) || stopped) return
    client.setToken("")
    reconnectTimer = setTimeout(() => {
      reconnectTimer = null
      client.connect()
    }, terminalReconnectDelay)
    terminalReconnectDelay = Math.min(terminalReconnectDelay * 2, TERMINAL_RECONNECT_MAX_MS)
  })
  client.on("error", (ctx) => {
    // Transport errors repeat on every reconnect attempt while a path cuts the
    // websocket (the RU box); the fallback to HTTP streaming is the answer, not a warning.
    const log = ctx.type === "transport" ? logger.debug : logger.warn
    log("Client error", { type: ctx.type, error: ctx.error })
  })
  client.connect()

  return { stop }
}
